import { useState } from 'react';
import { rule } from './rules';
import { usePaging } from './usePaging';

export type DirectoryView = 'all' | 'active' | 'new' | 'attention';

/**
 * What the customers and agents lists share: the tile choice (all / active / new / needs attention),
 * the search words, the status filter, the "add" dialog and the paging. `base` is the list after those;
 * a page adds its own filters (industry, country, services…) on top.
 */
export function useDirectory<T extends { status: string; addedDaysAgo: number }>(
  items: T[],
  size: number,
  needsAttention: (item: T) => boolean,
  searchText: (item: T) => string,
) {
  const paging = usePaging(size);
  const [view, setView] = useState<DirectoryView>('all');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [adding, setAdding] = useState(false);
  const w = search.trim().toLowerCase();
  const base = items
    .filter(
      (i) =>
        view === 'all' ||
        (view === 'active'
          ? i.status === 'Active'
          : view === 'new'
            ? i.addedDaysAgo <= rule('newWithinDays')
            : needsAttention(i)),
    )
    .filter((i) => status === 'all' || i.status === status)
    .filter((i) => !w || searchText(i).toLowerCase().includes(w));
  return {
    paging,
    view,
    search,
    status,
    adding,
    setAdding,
    base,
    /** the setters that also go back to page 1 */
    setView: paging.reset(setView),
    setSearch: paging.reset(setSearch),
    setStatus: paging.reset(setStatus),
  };
}
